import React, { useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ProductRow } from "../../../../services/v2/inventory.service";
import { ui } from "../kits/shared";
import ProductPicker from "./productPicker";
import { EMPTY_DRAFT, VariantAxes, VariantDraft, draftMissing, sortSizes, summaryText, variantFacts } from "./variantes";
import AvisoTalla from "./avisoTalla";
import { TallaEnNombre } from "../../../../utils/tallaEnNombre";

export interface ProductFormProps {
	campusId: number;
	catalogs: any;
	product: any | null;          // null = alta
	defaultKind: string;
	busy?: boolean;
	error?: string | null;
	onSave: (data: Record<string, any>, photo: File | null) => void;
	onCancel: () => void;
}

interface DraftComponent {
	articleId: number;
	name: string;
	waCode: string | null;
	quantity: number;
	sizeAtDelivery: boolean;
	/** La pieza trae tallas/colores: el kit los toma solos y se eligen al vender o entregar. */
	variantText: string | null;
	substitutes: { articleId: number; name: string }[];
}

/**
 * Alta y edición de producto. Un kit o un uniforme se arma aquí mismo con los artículos que lo componen.
 * Sin <label>: el CSS global los posiciona absolutos.
 */
const ProductForm: React.FC<ProductFormProps> = ({ campusId, catalogs, product, defaultKind, busy, error, onSave, onCancel }) => {
	const editing = product !== null;
	const KIND_KEYS = ["kit", "uniform", "article", "digital", "service"];
	const [kind, setKind] = useState<string>(product?.kind || (defaultKind === "courses" ? "kit" : KIND_KEYS.includes(defaultKind) ? defaultKind : "article"));
	const [name, setName] = useState<string>(product?.name || "");
	/** Solo Super Admin fija el precio de venta; para los demás lo que se captura es una sugerencia que ella aprueba. */
	const isSuper = !!catalogs?.isSuperAdmin;
	const approvedPrice: number = product && !product.pricePending ? Number(product.basePrice ?? product.price ?? 0) : 0;
	/*
		Empty = "sin precio": it is not sent, so saving (e.g. only the photo) never approves a pending product at $0.
		Super Admin sees the suggestion as a hint and decides by typing it (or in "Precios por aprobar").
	*/
	const [basePrice, setBasePrice] = useState<string>(product
		? String(!isSuper && product.suggestedPrice ? product.suggestedPrice : (product.pricePending ? "" : (product.basePrice ?? product.price ?? "")))
		: "");
	const [sharedCode, setSharedCode] = useState<boolean>(false);
	const facts = variantFacts((product?.variants || []).filter((variant: any) => !variant.deprecatedAt));
	// The size scale can only change while no variant has a size (a product with colors only can still get sizes).
	const hasSizes = facts.sizes.length > 0;
	const [brand, setBrand] = useState<string>(product?.brand || "");
	const [unit, setUnit] = useState<string>(product?.unit || "pieza");
	const [sizeType, setSizeType] = useState<string>(product?.sizeType || "none");
	// Sizes and colors picked right here (like the scanner): created together with the product, no second trip to "Variantes".
	const [draft, setDraft] = useState<VariantDraft>(EMPTY_DRAFT);
	const [barcode, setBarcode] = useState<string>(product?.barcode || "");
	const [serialized, setSerialized] = useState<boolean>(!!product?.serialized);
	const [description, setDescription] = useState<string>(product?.description || "");
	const [notes, setNotes] = useState<string>(product?.notes || "");
	const [digitalAccess, setDigitalAccess] = useState<string>(product?.digitalAccess || "wapp_books");
	const [photo, setPhoto] = useState<File | null>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [existingPhoto, setExistingPhoto] = useState<string | null>(null);
	// Only the ids travel: the back keeps what "Kits por Curso" configured (required, order, from module) for the courses that stay.
	const [courseIds, setCourseIds] = useState<number[]>((product?.courses || []).map((course: any) => course.courseId));
	const [components, setComponents] = useState<DraftComponent[]>((product?.version?.components || []).map((component: any) => ({
		articleId: component.articleId, name: component.name, waCode: component.waCode, quantity: component.quantity,
		sizeAtDelivery: component.sizeAtDelivery, variantText: summaryText(component.variantSummary),
		substitutes: (component.substitutes || []).map((s: any) => ({ articleId: s.articleId, name: s.name }))
	})));
	const [pickingFor, setPickingFor] = useState<number | null>(null);

	const physical = ["article", "uniform", "kit"].includes(kind);
	const composite = kind === "kit" || kind === "uniform";
	const needsPhoto = physical && !editing;
	// A kit or uniform without price would be offered (and charged) at $0 in Nueva Venta: it needs at least a suggestion.
	const needsPrice = composite && !editing;
	const valid = name.trim().length > 1 && (!needsPhoto || photo !== null) && (!composite || components.length > 0) && (!needsPrice || Number(basePrice) > 0)
		&& (kind !== "article" || sizeType === "none" || draft.sizes.length > 0 || facts.sizes.length > 0)
		&& (kind !== "article" || draftMissing(facts, draft) === null);

	useEffect(() => {
		if (!editing || !product?.hasPhoto) return;
		let url: string | null = null;
		HttpManager.getInstance().inventoryService.photoBlob(campusId, product.id)
			.then((response) => { url = URL.createObjectURL(response.data); setExistingPhoto(url); })
			.catch(() => undefined);
		return () => { if (url) URL.revokeObjectURL(url); };
	}, [campusId, product, editing]);

	useEffect(() => {
		if (!photo) { setPreview(null); return; }
		const url = URL.createObjectURL(photo);
		setPreview(url);
		return () => URL.revokeObjectURL(url);
	}, [photo]);

	const patchComponent = (index: number, changes: Partial<DraftComponent>) =>
		setComponents(components.map((component, position) => position === index ? { ...component, ...changes } : component));

	const addComponent = (picked: ProductRow) => {
		if (components.some((component) => component.articleId === picked.id)) return;
		setComponents([...components, {
			articleId: picked.id, name: picked.name, waCode: picked.waCode, quantity: 1,
			sizeAtDelivery: picked.variantCount > 0 || picked.requiresSizeAtDelivery,
			variantText: picked.variantCount > 0 ? picked.variantCount + " variantes" : null, substitutes: []
		}]);
	};

	const submit = () => {
		if (!valid) return;
		const data: Record<string, any> = {
			kind, name: name.trim(), unit, sizeType, description, notes, serialized: kind === "article" && serialized ? 1 : 0
		};
		if (basePrice.trim() !== "") data.basePrice = Number(basePrice);
		if (kind === "article" && (draft.sizes.length > 0 || draft.colors.length > 0)) {
			data.newSizes = sizeType !== "none" ? draft.sizes : [];
			data.newColors = draft.colors;
			data.existingColor = draft.existingColor;
			data.existingSize = draft.existingSize;
		}
		// Only articles show the barcode field; an empty value clears it.
		if (kind === "article") {
			data.barcode = barcode.trim();
			if (sharedCode) data.sharedCode = 1;
		}
		if (kind !== "service" && kind !== "digital") data.brand = brand;
		if (kind === "digital") data.digitalAccess = digitalAccess;
		if (composite) {
			data.courses = courseIds.map((id) => ({ courseId: id }));
			data.components = components.map((component) => ({
				articleId: component.articleId, quantity: component.quantity,
				sizeAtDelivery: component.sizeAtDelivery, substitutes: component.substitutes.map((s) => s.articleId)
			}));
		}
		onSave(data, photo);
	};

	// "Bata de Belleza S" → nombre "Bata de Belleza" y la talla S elegida abajo (si es de la escala; "38/39" no lo es).
	const fixName = (found: TallaEnNombre) => {
		setName(found.base);
		if (kind !== "article") return;
		const type = hasSizes || sizeType !== "none" ? sizeType : found.sizeType;
		if (!hasSizes && sizeType === "none") setSizeType(found.sizeType);
		const scale: string[] = (catalogs?.sizeTypes?.[type]?.values || []).map((entry: any) => String(entry.value));
		if (type === found.sizeType && scale.includes(found.size) && !facts.sizes.includes(found.size) && !draft.sizes.includes(found.size)) {
			setDraft({ ...draft, sizes: sortSizes(scale, [...draft.sizes, found.size]) });
		}
	};

	const field = (caption: string, node: React.ReactNode, width = "100%") => <div style={{ ...ui.field(width), minWidth: 0 }}>
		<span style={ui.caption}>{caption}</span>
		{node}
	</div>;

	return <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
		onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onCancel(); }}>
		<div className="card" style={{ width: "100%", maxWidth: composite ? "860px" : "760px", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }} role="dialog" aria-modal="true">
			<div className="card-body">
				<h4 style={{ marginBottom: "12px" }}>{editing ? "Editar Producto" : "Nuevo Producto"}</h4>

				<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "14px" }}>
					{(catalogs?.kinds || []).map((option: any) => <button key={option.id} type="button" disabled={busy || editing}
						className={"btn btn-sm rounded-pill " + (kind === option.id ? "btn-primary" : "btn-outline-secondary")}
						onClick={() => setKind(option.id)}>{option.name}</button>)}
				</div>

				<div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
					<div style={{ flex: "1 1 320px", display: "flex", flexDirection: "column", gap: "10px" }}>
						{field("Nombre del producto *", <input type="text" className="form-control" style={ui.input} value={name} disabled={busy} onChange={(event) => setName(event.target.value)} placeholder="Ej.: Máquina BabyLiss FX870" />)}
						{kind === "article" || kind === "uniform" ? <AvisoTalla name={name} busy={busy} onFix={fixName} /> : null}
						<div style={{ display: "flex", gap: "10px" }}>
							{field((isSuper ? "Precio de venta" : "Precio sugerido" + (approvedPrice > 0 ? " (hoy " + approvedPrice.toFixed(2) + ")" : "")) + (needsPrice ? " *" : ""),
								<>
									<input type="number" min={0} step="0.01" className="form-control" style={ui.input} value={basePrice} disabled={busy}
										placeholder={isSuper && product?.pricePending && product?.suggestedPrice ? "Sugerido " + Number(product.suggestedPrice).toFixed(2) : ""}
										onChange={(event) => setBasePrice(event.target.value)} />
									{isSuper && product?.pricePending
										? <span style={{ ...ui.tiny, marginTop: "3px" }}>Precio por aprobar{product?.suggestedPrice ? " (sugirieron " + Number(product.suggestedPrice).toFixed(2) + ")" : ""}. Si lo dejas vacío sigue pendiente.</span>
										: null}
								</>,
								kind === "service" || kind === "digital" ? "100%" : "48%")}
							{kind !== "service" && kind !== "digital"
								? field("Marca", <input type="text" className="form-control" style={ui.input} value={brand} disabled={busy} onChange={(event) => setBrand(event.target.value)} />, "48%")
								: null}
						</div>
						{kind === "article" ? <div style={{ display: "flex", gap: "10px" }}>
							{field("Unidad", <select className="form-select" style={ui.input} value={unit} disabled={busy} onChange={(event) => setUnit(event.target.value)}>
								{(catalogs?.units || ["pieza"]).map((option: string) => <option key={option} value={option}>{option}</option>)}
							</select>, "48%")}
							{field("Tallas", <select className="form-select" style={ui.input} value={sizeType} disabled={busy || hasSizes}
								title={hasSizes ? "Ya tiene tallas creadas: el tipo de talla no se puede cambiar." : undefined}
								onChange={(event) => setSizeType(event.target.value)}>
								{Object.keys(catalogs?.sizeTypes || { none: { label: "Sin talla" } }).map((key) => <option key={key} value={key}>{catalogs?.sizeTypes?.[key]?.label || key}</option>)}
							</select>, "48%")}
						</div> : null}
						{kind === "article" ? <div style={{ border: "1px solid var(--wa-filo)", borderRadius: "8px", padding: "8px 10px" }}>
							<VariantAxes catalogs={catalogs} sizeType={sizeType} facts={facts} draft={draft} busy={busy} onChange={setDraft} />
							{sizeType !== "none" && draft.sizes.length < 1 && facts.sizes.length < 1 ? <span style={{ ...ui.tiny, color: "var(--wa-oro)", display: "block", marginTop: "4px" }}>Elige al menos una talla *</span> : null}
						</div> : null}
						{kind === "article" ? field("Código de barras",
							<>
								<input type="text" className="form-control" style={ui.input} value={barcode} disabled={busy}
									onChange={(event) => setBarcode(event.target.value)} placeholder="Pon el cursor aquí y escanea el producto" />
								<span style={{ ...ui.tiny, marginTop: "3px" }}>El lector escribe solo: haz clic en el campo y pasa el código. Si el producto no trae, déjalo vacío y WAPP usa su código WA.</span>
								{sharedCode || (error || "").startsWith("Ese código ya es de") ? <div style={{ display: "flex", gap: "8px", alignItems: "center", cursor: "pointer", marginTop: "4px" }} onClick={() => { if (!busy) setSharedCode(!sharedCode); }}>
									<input type="checkbox" className="form-check-input" checked={sharedCode} readOnly style={{ margin: 0 }} />
									<span style={{ fontSize: "13px" }}>Código compartido: otro producto trae el mismo código de fábrica</span>
								</div> : null}
							</>) : null}
						{kind === "article" && sizeType === "none" ? <div style={{ display: "flex", gap: "8px", alignItems: "center", cursor: "pointer" }} onClick={() => { if (!busy) setSerialized(!serialized); }}>
							<input type="checkbox" className="form-check-input" checked={serialized} readOnly style={{ margin: 0 }} />
							<span style={{ fontSize: "13px" }}>Se controla por número de serie</span>
						</div> : null}
						{kind === "digital" ? field("Cómo recibe el acceso el alumno",
							<select className="form-select" style={ui.input} value={digitalAccess} disabled={busy} onChange={(event) => setDigitalAccess(event.target.value)}>
								{(catalogs?.digitalAccess || []).map((option: any) => <option key={option.id} value={option.id}>{option.name}</option>)}
							</select>) : null}
						{field("Descripción · la ve quien vende y el alumno",
							<textarea className="form-control" rows={2} style={{ fontSize: "13px", resize: "vertical" }} value={description} disabled={busy} onChange={(event) => setDescription(event.target.value)} placeholder="Qué incluye o para qué sirve" />)}
						{field("Notas internas · solo el equipo",
							<textarea className="form-control" rows={2} style={{ fontSize: "13px", resize: "vertical" }} value={notes} disabled={busy} onChange={(event) => setNotes(event.target.value)} placeholder="Proveedor, dónde se compra, cuidados" />)}
					</div>

					<div style={{ flex: "0 0 200px", display: "flex", flexDirection: "column", gap: "8px" }}>
						<span style={ui.caption}>Foto {needsPhoto ? <span style={{ color: "var(--wa-oro)" }}>*</span> : null}</span>
						<div style={{ width: "200px", height: "200px", border: "1px dashed " + (needsPhoto && !photo ? "var(--wa-oro)" : "var(--wa-filo-2)"), borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: "var(--wa-papel-2)" }}>
							{preview || existingPhoto
								? <img src={(preview || existingPhoto) as string} alt="Foto del producto" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
								: <span style={{ ...ui.small, textAlign: "center", padding: "0 12px" }}>{physical ? "Obligatoria" : "Opcional"}</span>}
						</div>
						<input type="file" accept="image/*" capture="environment" className="form-control" style={{ fontSize: "12px" }} disabled={busy}
							onChange={(event) => setPhoto(event.target.files && event.target.files[0] ? event.target.files[0] : null)} />
						{editing && product?.waCode ? <div style={ui.small}>Código: <b>{product.waCode}</b></div> : null}
					</div>
				</div>

				{composite ? <div style={{ marginTop: "16px" }}>
					<div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "6px" }}>
						{kind === "kit" ? "Artículos que forman el kit" : "Piezas que forman el uniforme"} <span style={{ color: "var(--wa-oro)" }}>*</span>
					</div>
					<ProductPicker campusId={campusId} disabled={busy} kinds={kind === "kit" ? ["article", "uniform"] : ["article"]}
						excludeIds={[product?.id, ...components.map((component) => component.articleId)].filter(Boolean) as number[]}
						placeholder={kind === "kit" ? "Buscar artículo o uniforme para agregar…" : "Buscar artículo para agregar…"}
						onPick={addComponent} />
					<div style={{ ...ui.card, marginTop: "8px", overflowX: "auto" }}>
						<table style={{ width: "100%", borderCollapse: "collapse" }}>
							<thead><tr>
								<th style={ui.th}>Pieza</th>
								<th style={{ ...ui.th, ...ui.center }}>Cantidad</th>
								<th style={ui.th}>Talla / Color</th>
								<th style={ui.th}>Sustitutos aceptables</th>
								<th style={ui.th}></th>
							</tr></thead>
							<tbody>
								{components.length < 1
									? <tr><td style={{ ...ui.td, ...ui.center, color: "var(--wa-apagado)" }} colSpan={5}>Busca arriba y agrega las piezas. Si una pieza no existe todavía, créala primero como Artículo.</td></tr>
									: components.map((component, index) => <tr key={component.articleId}>
										<td style={ui.td}>
											<div style={{ fontWeight: 600 }}>{component.name}</div>
											<div style={ui.tiny}>{component.waCode}</div>
										</td>
										<td style={{ ...ui.td, ...ui.center }}>
											<input type="number" min={1} className="form-control" style={{ ...ui.input, width: "72px", margin: "0 auto" }} value={component.quantity} disabled={busy}
												onChange={(event) => patchComponent(index, { quantity: Math.max(1, Number(event.target.value) || 1) })} />
										</td>
										<td style={ui.td}>
											{component.variantText
												// Its sizes/colors come with it (also the ones added later): picked when selling or delivering.
												? <><div style={{ fontSize: "12px" }}>{component.variantText}</div><div style={ui.tiny}>se elige al vender o entregar</div></>
												: <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
													<input type="checkbox" checked={component.sizeAtDelivery} disabled={busy} onChange={(event) => patchComponent(index, { sizeAtDelivery: event.target.checked })} />
													<span style={ui.tiny}>talla al entregar</span>
												</div>}
										</td>
										<td style={ui.td}>
											{component.substitutes.map((substitute) => <span key={substitute.articleId}
												style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "var(--wa-acento-suave)", borderRadius: "999px", padding: "2px 8px", fontSize: "12px", marginRight: "4px", marginBottom: "4px" }}>
												{substitute.name}
												<span style={{ cursor: "pointer", color: "var(--wa-apagado)" }} onClick={() => patchComponent(index, { substitutes: component.substitutes.filter((s) => s.articleId !== substitute.articleId) })}>×</span>
											</span>)}
											{pickingFor === index
												? <span style={{ ...ui.tiny, fontWeight: 600 }}>Búscalo abajo de la tabla ↓</span>
												: <button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} disabled={busy} onClick={() => setPickingFor(index)}>+ agregar sustituto</button>}
										</td>
										<td style={ui.td}>
											<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill waOroTexto" disabled={busy} onClick={() => { setPickingFor(null); setComponents(components.filter((_, position) => position !== index)); }}>Quitar</button>
										</td>
									</tr>)}
							</tbody>
						</table>
					</div>
					{pickingFor !== null && components[pickingFor] ? <div style={{ marginTop: "8px" }}>
						{/* Outside the table: its horizontal scroll used to clip the result list to one or two rows. */}
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
							<span style={ui.caption}>Sustituto aceptable para <b>{components[pickingFor].name}</b></span>
							<button type="button" className="btn btn-link btn-sm" style={{ padding: 0, fontSize: "12px" }} disabled={busy} onClick={() => setPickingFor(null)}>Cancelar</button>
						</div>
						<ProductPicker campusId={campusId} disabled={busy} placeholder="Sustituto aceptable…" kinds={["article", "uniform"]}
							excludeIds={[components[pickingFor].articleId, ...components[pickingFor].substitutes.map((s) => s.articleId)]}
							onPick={(picked) => { const index = pickingFor; const component = components[index]; patchComponent(index, { substitutes: [...component.substitutes, { articleId: picked.id, name: picked.name }] }); setPickingFor(null); }} />
					</div> : null}

					<div style={{ marginTop: "12px" }}>
						<span style={ui.caption}>Cursos que llevan este {kind === "kit" ? "kit" : "uniforme"}</span>
						<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "4px", maxHeight: "120px", overflowY: "auto", padding: "4px", border: "1px solid var(--wa-filo)", borderRadius: "8px" }}>
							{(catalogs?.courses || []).map((course: any) => {
								const active = courseIds.includes(course.id);
								return <button key={course.id} type="button" disabled={busy}
									className={"btn btn-sm rounded-pill " + (active ? "btn-primary" : "btn-outline-secondary")}
									title={course.fullName}
									onClick={() => setCourseIds(active ? courseIds.filter((id) => id !== course.id) : [...courseIds, course.id])}>{course.name}</button>;
							})}
						</div>
					</div>
				</div> : null}

				{error ? <div className="alert alert-danger" style={{ marginTop: "12px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
				<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
					<button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={onCancel}>Cancelar</button>
					<button type="button" className="btn btn-primary rounded-pill" disabled={busy || !valid} onClick={submit}>{busy ? "Guardando…" : editing ? "Guardar Cambios" : "Crear Producto"}</button>
				</div>
			</div>
		</div>
	</div>;
};

export default ProductForm;
